import type {
  Column,
  ImageBlock,
  ImageObjectFit,
  Row,
} from "./types";
import { effectiveImageBoxWidthPx } from "./image-width";

export const IMAGE_HEIGHT_MIN = 40;
export const IMAGE_HEIGHT_MAX = 800;
export const IMAGE_HEIGHT_DEFAULT = 200;
export const IMAGE_WIDTH_SNAP = 5;
export const IMAGE_HEIGHT_SNAP = 8;

export const IMAGE_OBJECT_FIT_OPTIONS: {
  value: ImageObjectFit;
  label: string;
}[] = [
  { value: 'cover', label: 'Cover' },
  { value: 'contain', label: 'Contain' },
];

export function clampImageHeight(px: number): number {
  return Math.min(IMAGE_HEIGHT_MAX, Math.max(IMAGE_HEIGHT_MIN, px));
}

export function snapImageHeight(px: number, fine: boolean): number {
  const step = fine ? 1 : IMAGE_HEIGHT_SNAP;
  return clampImageHeight(Math.round(px / step) * step);
}

export function isSoleBlockInColumn(column: Column, blockId: string): boolean {
  return column.blocks.length === 1 && column.blocks[0]?.id === blockId;
}

export function canUseFillColumn(
  row: Row | undefined,
  column: Column | undefined,
  block: ImageBlock,
): boolean {
  if (!row || !column) {
    return false;
  }
  return row.columns.length > 1 && isSoleBlockInColumn(column, block.id);
}

export function columnHasFillColumnImage(row: Row, column: Column): boolean {
  if (row.columns.length < 2 || column.blocks.length !== 1) {
    return false;
  }
  const block = column.blocks[0];
  return block?.type === 'image' && block.heightMode === 'fillColumn';
}

export function sanitizeFillColumnImagesInRow(row: Row): void {
  for (const column of row.columns) {
    for (const block of column.blocks) {
      if (
        block.type === 'image' &&
        block.heightMode === 'fillColumn' &&
        !canUseFillColumn(row, column, block)
      ) {
        block.heightMode = 'auto';
        block.heightPx = null;
      }
    }
  }
}

export function defaultImageHeightPx(block: ImageBlock): number {
  return block.heightPx ?? IMAGE_HEIGHT_DEFAULT;
}

export function initialFixedImageHeightPx(
  block: ImageBlock,
  contentMaxWidth: number,
  renderedHeightPx?: number | null,
  naturalSize?: { width: number; height: number } | null,
): number {
  if (renderedHeightPx != null && renderedHeightPx > 0) {
    return clampImageHeight(Math.round(renderedHeightPx));
  }
  if (
    block.heightMode === 'auto' &&
    naturalSize &&
    naturalSize.width > 0 &&
    contentMaxWidth > 0
  ) {
    const boxWidth = effectiveImageBoxWidthPx(block, contentMaxWidth);
    return clampImageHeight(
      Math.round((boxWidth * naturalSize.height) / naturalSize.width),
    );
  }
  return defaultImageHeightPx(block);
}

export function isFineResizeStep(event: {
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}): boolean {
  return event.metaKey || event.ctrlKey;
}
